import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarDays, Check, ChevronLeft, ChevronRight, CirclePlus } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { ulid } from "ulid";
import { DateInput } from "../components/DateInput";
import { ErrorScreen, LoadingScreen } from "../components/ScreenState";
import { api } from "../lib/api";
import { addDays, readableDate } from "../lib/date";
import { queryClient } from "../lib/query";
import { getPreferredChildId, savePreferredChildId } from "../lib/preferred-child";

export function TodayPage() {
  const data = useQuery({ queryKey: ["bootstrap"], queryFn: api.bootstrap });
  const [preferredChildId, setPreferredChildId] = useState(getPreferredChildId);
  const [selectedDate, setSelectedDate] = useState("");
  const [showDateSelector, setShowDateSelector] = useState(false);
  const today = data.data?.today ?? "";
  const effectiveDate = selectedDate || today;
  const selectedPlan = useQuery({
    queryKey: ["plan-items", effectiveDate],
    queryFn: () => api.planItems(effectiveDate),
    enabled: Boolean(effectiveDate && effectiveDate !== today),
  });
  const toggle = useMutation({
    mutationFn: (id: string) => api.toggleItem(id, ulid()),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ["bootstrap"] });
      const previous = queryClient.getQueryData(["bootstrap"]);
      queryClient.setQueryData(["bootstrap"], (current: typeof data.data) =>
        current
          ? {
              ...current,
              todayItems: current.todayItems.map((item) =>
                item.id === id ? { ...item, status: item.status === "DONE" ? "TODO" : "DONE" } : item,
              ),
            }
          : current,
      );
      return { previous };
    },
    onError: (_error, _id, context) => queryClient.setQueryData(["bootstrap"], context?.previous),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
      void queryClient.invalidateQueries({ queryKey: ["plan-items", effectiveDate] });
    },
  });

  if (data.isPending) return <LoadingScreen />;
  if (data.isError) return <ErrorScreen message={data.error.message} onRetry={() => void data.refetch()} />;

  const child = data.data.children.find((item) => item.id === preferredChildId) ?? data.data.children[0];
  const visibleItems = effectiveDate === data.data.today ? data.data.todayItems : selectedPlan.data?.items ?? [];
  const items = visibleItems.filter((item) => item.childId === child?.id);
  const done = items.filter((item) => item.status === "DONE").length;
  const total = items.length;
  const percent = total ? Math.round((done / total) * 100) : 0;

  function selectChild(childId: string) {
    setPreferredChildId(childId);
    savePreferredChildId(childId);
  }

  return (
    <section className="screen today-screen">
      <header className="today-title">
        <h1>今天</h1>
      </header>
      <div className="today-meta">
        <label className="child-switch">
          <span className="avatar">{child?.nickname.slice(0, 1) ?? "小"}</span>
          <select aria-label="切换孩子" value={child?.id ?? ""} onChange={(event) => selectChild(event.target.value)}>
            {data.data.children.map((item) => <option value={item.id} key={item.id}>{item.nickname}</option>)}
          </select>
        </label>
        <button className="date-label" type="button" aria-expanded={showDateSelector} aria-controls="today-date-selector" onClick={() => setShowDateSelector((current) => !current)}>
          {readableDate(effectiveDate)} <CalendarDays size={17} aria-hidden="true" />
        </button>
      </div>
      {showDateSelector ? (
        <div className="date-selector" id="today-date-selector">
          <button type="button" aria-label="前一天" onClick={() => setSelectedDate(addDays(effectiveDate, -1))}><ChevronLeft /></button>
          <label className="field"><span className="visually-hidden">选择计划日期</span><DateInput aria-label="选择计划日期" value={effectiveDate} onChange={(event) => setSelectedDate(event.target.value)} /></label>
          <button type="button" aria-label="后一天" onClick={() => setSelectedDate(addDays(effectiveDate, 1))}><ChevronRight /></button>
          <button className="date-selector-today" type="button" onClick={() => { setSelectedDate(data.data.today); setShowDateSelector(false); }}>回到今天</button>
        </div>
      ) : null}
      <div className="progress-block" aria-label={`${effectiveDate === data.data.today ? "今日" : readableDate(effectiveDate)} ${total} 项，已完成 ${done} 项`}>
        <div className="progress-copy">
          <strong>{effectiveDate === data.data.today ? "今日" : readableDate(effectiveDate)} {total} 项 · 已完成 {done} 项</strong>
          <span>{done}/{total || 0}</span>
        </div>
        <div className="progress-track"><span style={{ width: `${percent}%` }} /></div>
      </div>

      <Link className="primary-button capture-cta" to="/capture">
        <CirclePlus size={24} /> 录入作业
      </Link>

      {selectedPlan.isPending && effectiveDate !== data.data.today ? (
        <div className="empty-state compact"><p>正在加载这一天的计划…</p></div>
      ) : total > 0 ? (
        <div className="task-list">
          <div className="task-head"><span>科目</span><span>作业内容</span><span>计划量</span><span>完成</span></div>
          {items.map((item) => (
            <article className={`task-row ${item.status === "DONE" ? "is-done" : ""}`} key={item.id}>
              <div className="task-subject"><i style={{ background: item.color }} /><strong>{item.subject}</strong></div>
              <p>{item.title}</p>
              <span>{item.plannedAmount} {item.unit}</span>
              <button
                type="button"
                className="complete-button"
                aria-label={item.status === "DONE" ? `撤销完成：${item.title}` : `完成：${item.title}`}
                aria-pressed={item.status === "DONE"}
                onClick={() => toggle.mutate(item.id)}
              >
                {item.status === "DONE" ? <Check size={22} strokeWidth={2.5} /> : null}
              </button>
            </article>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-symbol"><Check size={27} /></div>
          <h2>{effectiveDate === data.data.today ? "今天还没有任务" : "这一天没有安排任务"}</h2>
          <p>{effectiveDate === data.data.today ? "录入一项作业，系统会按截止日期自动拆成每日计划。" : "当前只显示所选日期的计划，点击右上日期可查看其他日期。"}</p>
          {effectiveDate === data.data.today ? <Link to="/capture" className="text-link">立即录入</Link> : null}
        </div>
      )}
    </section>
  );
}
